import { TFunction } from 'i18next'
import { z } from 'zod'

import { isValidPhoneInput } from '../../shared/person-table.utils'

export const PROPERTY_MANAGER_NAME_MAX_LENGTH = 50
export const PROPERTY_MANAGER_JOB_TITLE_MAX_LENGTH = 100

export type PropertyManagerFormMode = 'create' | 'edit'

export const makePropertyManagerFormSchema = (
  t: TFunction<['organisation']>,
  mode: PropertyManagerFormMode,
) =>
  z
    .object({
      avatar: z.array(z.instanceof(File)).optional(),
      buildingIds: z
        .array(z.string())
        .min(1, t('organisation:propertyManagers.form.validation.buildingsRequired')),
      email: z
        .string()
        .trim()
        .min(1, t('organisation:propertyManagers.form.validation.emailRequired')),
      firstName: z
        .string()
        .trim()
        .min(1, t('organisation:propertyManagers.form.validation.firstNameRequired'))
        .max(
          PROPERTY_MANAGER_NAME_MAX_LENGTH,
          t('organisation:propertyManagers.form.validation.nameMaxLength', {
            max: PROPERTY_MANAGER_NAME_MAX_LENGTH,
          }),
        ),
      jobTitle: z
        .string()
        .max(
          PROPERTY_MANAGER_JOB_TITLE_MAX_LENGTH,
          t('organisation:propertyManagers.form.validation.jobTitleMaxLength', {
            max: PROPERTY_MANAGER_JOB_TITLE_MAX_LENGTH,
          }),
        )
        .optional(),
      lastName: z
        .string()
        .trim()
        .min(1, t('organisation:propertyManagers.form.validation.lastNameRequired'))
        .max(
          PROPERTY_MANAGER_NAME_MAX_LENGTH,
          t('organisation:propertyManagers.form.validation.nameMaxLength', {
            max: PROPERTY_MANAGER_NAME_MAX_LENGTH,
          }),
        ),
      password: z.string().optional(),
      phone: z
        .string()
        .optional()
        .refine(
          (value) => !value || isValidPhoneInput(value),
          t('organisation:propertyManagers.form.validation.phoneInvalid'),
        ),
    })
    .superRefine((data, ctx) => {
      if (mode !== 'create') return

      if (!z.email().safeParse(data.email).success && data.email) {
        ctx.addIssue({
          code: 'custom',
          message: t('organisation:propertyManagers.form.validation.emailInvalid'),
          path: ['email'],
        })
      }
      if (!data.password) {
        ctx.addIssue({
          code: 'custom',
          message: t('organisation:propertyManagers.form.validation.passwordRequired'),
          path: ['password'],
        })
      }
    })

export type PropertyManagerFormData = z.infer<ReturnType<typeof makePropertyManagerFormSchema>>
